import { Link } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { ManagerAuthorization } from '@common/enums';
import { assertHasAuthorization } from '@frontend/utils';
import type { IServiceOrganizationSettings } from '../../interfaces/serviceOrganizationSettings';
import { never } from '../../../../common/utils/never';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import {
    AdminTable,
    EditButton,
    Header,
    LinkButton,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '../../elements/common';
import { NullableFormattedDate } from '../../elements/NullableFormattedDate';

type OrganizationOrService = 'organization' | 'service';

interface IServiceOrganizationSettingsListProps {
    filterId: string;
    filterType: OrganizationOrService;
    onlyReadUpdate?: boolean;
}

export function ServiceOrganizationSettingsList(props: IServiceOrganizationSettingsListProps) {
    const [serviceOrganizationSettings, setServiceOrganizationSettings] = useState<IServiceOrganizationSettings[]>([]);

    assertHasAuthorization(ManagerAuthorization.serviceRightsOrganizations);

    let headerText: string;
    let displayPropertyName: OrganizationOrService;
    let linkBegin: string;
    switch (props.filterType) {
        case 'organization':
            headerText = translateKey('service.service');
            displayPropertyName = 'service';
            linkBegin = 'services';
            break;
        case 'service':
            headerText = translateKey('organization.organization');
            displayPropertyName = 'organization';
            linkBegin = 'organizations';
            break;
        default:
            return never(props.filterType);
    }

    useEffect(() => {
        getSettings(props.filterId, props.filterType);
    }, [props.filterId, props.filterType]);

    return (
        <>
            <Header
                actionButtons={
                    props.onlyReadUpdate
                        ? undefined
                        : [
                              <LinkButton
                                  icon="add"
                                  to={`/serviceOrganizationSettings/add?${
                                      props.filterType === 'service' ? 'serviceId' : 'organizationId'
                                  }=${props.filterId}`}
                              >
                                  {translateKey(
                                      'common.addItem',
                                      translateKey('serviceProvider.serviceOrganizationSettings')
                                  )}
                              </LinkButton>,
                          ]
                }
                content={headerText}
            />
            <AdminTable>
                <TableHead>
                    <TableRow>
                        <TableHeader>{headerText}</TableHeader>
                        <TableHeader>{translateKey('common.startDate')}</TableHeader>
                        <TableHeader>{translateKey('common.endDate')}</TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {serviceOrganizationSettings.map((serviceOrganizationSetting) => (
                        <TableRow key={serviceOrganizationSetting.id}>
                            <TableData>
                                <Link
                                    to={`/${linkBegin}/${serviceOrganizationSetting[displayPropertyName].id}?tab=providerOrganizationSettings`}
                                >
                                    {serviceOrganizationSetting[displayPropertyName].name}
                                </Link>
                            </TableData>
                            <TableData>
                                <NullableFormattedDate value={serviceOrganizationSetting.startDate} />
                            </TableData>
                            <TableData>
                                <NullableFormattedDate value={serviceOrganizationSetting.endDate} />
                            </TableData>
                            <TableData align="right">
                                <EditButton
                                    to={`/serviceOrganizationSettings/${serviceOrganizationSetting.id}?${
                                        props.filterType === 'service' ? 'serviceId' : 'organizationId'
                                    }=${props.filterId}`}
                                />
                            </TableData>
                        </TableRow>
                    ))}
                </TableBody>
            </AdminTable>
        </>
    );

    async function getSettings(filterId: string, filterType: 'organization' | 'service') {
        let url = '/api/serviceOrganizationSettings';

        switch (filterType) {
            case 'organization':
                url += `?organizationId=${filterId}`;
                break;
            case 'service':
                url += `?serviceId=${filterId}`;
                break;
            default:
                return never(filterType);
        }

        const result = await fetchJson(url, 'GET');
        setServiceOrganizationSettings(result.json.items);
    }
}
